'use client'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Image from 'next/image'
import { Fragment, useEffect } from 'react'
import AnimatedDiv from 'src/components/AnimatedDiv'
import AggregateDataListContainer from './components/AggregateDataListContainer'
import EmailVerificationComp from './components/EmailVerificationComp'
import FastCasualTableWrapper from './components/FastCasualTableWrapper'
import IconChip from './components/IconChip'
import FastCasualContextProvider, {
  useFastCasualContext
} from './context/FastCasualContext'

export default function FastCasualIndexV2() {
  useEffect(() => {
    const bodyElement = document.documentElement
    function sendHeight() {
      window.parent.postMessage(
        {
          height: bodyElement.offsetHeight,
          width: bodyElement.offsetWidth,
          company: 'loop'
        },
        '*'
      )
    }

    const resizeObserver = new ResizeObserver(sendHeight)
    resizeObserver.observe(bodyElement)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <Fragment>
      <FastCasualContextProvider>
        <FastCasualIndexContent />
      </FastCasualContextProvider>
    </Fragment>
  )
}

function FastCasualIndexContent() {
  const { verified } = useFastCasualContext()

  return (
    <div className="relative bg-white md:px-24 px-7 pt-10">
      <div
        className="absolute top-0 left-0 right-0 flex flex-row justify-between"
        style={{ zIndex: 0 }}>
        <Image
          src="/img/fast_casual/top_left_lines.svg"
          alt=""
          width={690}
          height={704}
          style={{ maxWidth: '50%' }}
        />
        <Image
          src="/img/fast_casual/top_right_lines.svg"
          alt=""
          width={690}
          height={704}
          style={{ maxWidth: '50%' }}
        />
      </div>
      <AnimatedDiv
        className="flex justify-center mt-3 z-10 relative opacity-0"
        from={{ opacity: 0, scale: 0.5 }}
        to={{ opacity: 1, scale: 1, duration: 1 }}>
        {/* <IconChip title="The Fast Casual Index" /> */}
        <Image
          src="/img/logos/loop_fast_casual_logo.svg"
          alt=""
          width={254 * 4}
          height={133 * 4}
          style={{
            // width: '254px',
            // height: '133px',
            height: '200px',
            objectFit: 'contain'
          }}
        />
      </AnimatedDiv>
      <div className="mt-6 z-10 relative">
        <p className="text-3xl md:text-6xl text-center">
          A comprehensive snapshot of America's <br />
          leading fast casual brands
        </p>

        <div
          className={`mt-20 ${
            verified ? 'flex justify-center' : 'grid grid-cols-1 md:grid-cols-2'
          } gap-4 md:gap-8 lg:gap-12`}>
          {!verified && (
            <div className="px-0 md:px-8 flex flex-col items-center gap-3">
              <p
                className="md:text-base text-xs leading-5 text-center"
                style={{ color: '#605A57' }}>
                Explore customer reviews, app performance, social media impact,
                and more for 200+ brands in one place
              </p>
              <div className="flex-1" />
              <EmailVerificationComp
                buttonLabel="Sign Up To View Full Index"
                successLabel="Request is successfully sent"
                position="left"
              />
            </div>
          )}

          <div className="px-0 md:px-8 flex flex-col items-center gap-3">
            <p
              className="md:text-base text-xs leading-5 text-center"
              style={{ color: '#605A57' }}>
              Join the ranks, own your story and showcase your brand's
              excellence in the Fast Casual Index
            </p>
            <div className="flex-1" />
            {/* <Tooltip
              arrow
              placement="top"
              title="Join the ranks, own your story and showcase your brand's
              excellence in the Fast Casual Index—where 3PD performance
              drives recognition"> */}
            <a
              href="https://nj1kjshl.paperform.co/"
              target="_blank"
              style={{ textDecoration: 'none' }}>
              <button
                className="w-60 px-4 py-3 rounded text-sm font-normal"
                style={{ background: '#196E82', color: '#FFFFFF' }}>
                {/* Register Your Brand */}
                Add Your Brand To The List
              </button>
            </a>
            {/* </Tooltip> */}
          </div>
        </div>
      </div>

      <div className="mt-14 relative">
        <Image
          src="/img/fast_casual/graph.png"
          alt="Graph img"
          width={1456}
          height={189}
          className="hidden sm:block"
          style={{ width: '100%', objectFit: 'contain' }}
        />
        <Image
          src="/img/fast_casual/graph_mobile.png"
          alt="Graph img"
          width={1456}
          height={189}
          className="block sm:hidden"
          style={{ width: '100%', objectFit: 'contain' }}
        />
        <a
          // href="#index-highlights"
          className="absolute top-1/4 sm:top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <button
            className="px-4 py-1 rounded"
            style={{
              border: '1px solid rgba(230, 230, 230, 0.90)',
              background: '#F3F3F3'
            }}
            onClick={() => {
              document.getElementById('index-highlights')?.scrollIntoView({
                behavior: 'smooth'
              })
            }}>
            <span className="text-sm leading-5" style={{ color: '#343434' }}>
              Scroll to explore
            </span>
            &nbsp;{' '}
            <KeyboardArrowDownIcon
              sx={{ fontSize: '20px', color: '#343434' }}
            />
          </button>
        </a>
      </div>
      <div id="index-highlights" className="mt-14 flex flex-row items-center">
        <hr className="flex-1" />
        <IconChip title="Index Highlights" />
        <hr className="flex-1" />
      </div>

      <div className="mt-10">
        <AggregateDataListContainer />
      </div>

      <hr className="mt-10" />

      <AnimatedDiv
        className="mt-10 flex items-center justify-center"
        from={{ scale: 0.25, opacity: 0 }}
        to={{ scale: 1, opacity: 1, duration: 1, delay: 0.5 }}>
        <Image
          src="/img/logos/fast_casual_index_logo.svg"
          alt=""
          width={531 * 2}
          height={220 * 2}
          style={{ height: '150px', objectFit: 'contain' }}
        />
      </AnimatedDiv>

      <div
        // className="mt-11 md:px-10 px-5 md:py-8 py-4 flex md:flex-row flex-col gap-4 justify-between items-center relative rounded"
        className="mt-11 md:px-10 px-5 md:py-8 py-4 flex md:flex-col flex-col gap-4 justify-between items-center relative rounded"
        style={{ border: '1px solid #E6E6E6E6' }}>
        <div
          // className="absolute md:left-10 left-5 top-0 -translate-y-1/2"
          className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
          <IconChip title="Help Us Get Your Brand Data Right" />
        </div>
        <p className="md:text-base text-xs leading-5 text-left text-black">
          The Fast Casual Index aggregates data methodically and comprehensively
          from publicly available sources, evaluating multiple key data points
          across each category to deliver an accurate aggregate % score. If
          something seems off, click below to provide updates and corrections
        </p>
        <a href="https://fhmjrhdp.paperform.co/" target="_blank">
          <button
            className="px-4 py-3 rounded text-sm font-normal whitespace-nowrap"
            style={{ background: '#E75926', color: '#FFFFFF' }}>
            {/* Update Brand Data */}
            Help us represent your brand accurately
          </button>
        </a>
      </div>

      <div className="mt-16">
        <FastCasualTableWrapper />
      </div>
    </div>
  )
}
